import { useCallback, useEffect, useState } from 'react';

import { usePrevious } from 'react-use';

import { useNftDataStream, useOffer } from '@pkg/hooks';
import { type BaseCurrencyType } from '@pkg/types';
import { assertNumber } from '@pkg/utils';

import { useExchangeRatesStore, useOfferStore } from '@app/store/zustand';

import { type InputPriceType } from './AssetOfferSheet.types';
import { filterInputPrice, formatInputPrice } from './AssetOfferSheet.utilities';

export function useAssetOfferState() {
    const convertCurrency = useExchangeRatesStore((state) => state.convertCurrency);

    const { confirmOffer } = useOffer();
    const offerToken = useOfferStore((state) => state.token);
    const removeToken = useOfferStore((state) => state.removeToken);
    const setPendingConfirmation = useOfferStore((state) => state.setPendingConfirmation);

    const { loading, nativeCurrency, appraisal, floorData, lastSaleData, errors } = useNftDataStream({
        contractAddress: offerToken?.collection?.contractAddress,
        tokenId: offerToken?.tokenId,
    });

    const [localCurrency, setLocalCurrency] = useState<BaseCurrencyType>('usd');
    const prevLocalCurrency = usePrevious(localCurrency);

    const [localPrice, setLocalPrice] = useState<InputPriceType>();
    const [nativePrice, setNativePrice] = useState<number>();
    const [usdPrice, _setUsdPrice] = useState(0);
    const [localAppraisal, setLocalAppraisal] = useState<number>(0);

    const appraisedEth = appraisal.ethValue;

    const formatAndUpdateLocalPrice = useCallback((value?: InputPriceType, currencyType: BaseCurrencyType = 'usd') => {
        const newValue = formatInputPrice(value, currencyType);
        setLocalPrice(newValue);
    }, []);

    function handlePriceChange(value?: InputPriceType) {
        setLocalPrice(value);
    }

    async function handleSubmit() {
        const confirmedPrice = nativePrice ?? 0;
        setPendingConfirmation(true);

        return confirmOffer(confirmedPrice);
    }

    function handleReset() {
        removeToken();

        // No need to reset `usdPrice` or `localAppraisal`,
        // as they will be reset upon `localPrice` change.
        setLocalCurrency('usd');
        setLocalPrice(undefined);
        setNativePrice(undefined);
    }

    // Tracking conversion of localAppraisal.
    useEffect(() => {
        setLocalAppraisal(convertCurrency(nativeCurrency, localCurrency, appraisedEth, true));
    }, [convertCurrency, nativeCurrency, localCurrency, appraisedEth]);

    // Tracking conversion of localPrice when localCurrency is changed.
    useEffect(() => {
        if (assertNumber(localPrice) && prevLocalCurrency && prevLocalCurrency !== localCurrency) {
            const convertedPrice = convertCurrency(prevLocalCurrency, localCurrency, localPrice, true);

            formatAndUpdateLocalPrice(convertedPrice, localCurrency);
        }
    }, [convertCurrency, prevLocalCurrency, localCurrency, localPrice, formatAndUpdateLocalPrice]);

    // Tracking conversion of nativePrice when localPrice updates.
    useEffect(() => {
        const filtered = filterInputPrice(localPrice);
        const fromCurrency =
            prevLocalCurrency && prevLocalCurrency !== localCurrency ? prevLocalCurrency : localCurrency;
        const newValue =
            filtered === undefined ? undefined : convertCurrency(fromCurrency, nativeCurrency, filtered, true);

        setNativePrice(newValue);
    }, [convertCurrency, prevLocalCurrency, localCurrency, nativeCurrency, localPrice]);

    return {
        errors,
        loading,
        offerToken,
        removeToken,
        nativeCurrency,
        floorUsdValue: floorData.usdValue,
        floorUsd24hChange: floorData.usd24hChange,
        floorPercentChange: floorData.percentChange,
        lastSaleUsd: lastSaleData.usdValue,
        lastSaleUtcMs: lastSaleData.utcMs,
        localCurrency,
        localAppraisal,
        localPrice,
        nativePrice,
        usdPrice,
        appraisedEth,
        formatAndUpdateLocalPrice,
        handlePriceBlur: () => formatAndUpdateLocalPrice(localPrice, localCurrency),
        handlePriceChange,
        handleLocalCurrencyChange: setLocalCurrency,
        handleSubmit,
        handleReset,
    };
}
